import { useEffect, useState } from 'react';
import { ocrCaption, translateCaption, renderScreenshot, saveImageDraft, type DraftImageItem } from '@/api/play';
import { isRtlLocale } from '@/lib/rtlLocales';
import { Modal } from '../Modal';
import { Button } from '../Button';

interface SessionResult {
  key: string;
  url: string;
  contentType: string;
  localized: boolean; // true = a real caption render; false = English pass-through
}

interface Row {
  key: string;              // source screenshot key (stable identity)
  url: string;              // source screenshot URL
  english: string;          // OCR'd caption (editable)
  translated: string;       // transcreated caption (editable)
  selected: boolean;        // include in the next render pass
  loading: boolean;         // OCR/translate in flight
  status: 'idle' | 'rendering' | 'done' | 'error';
  result?: SessionResult;   // localized/pass-through produced this session
  existing?: DraftImageItem; // the target locale's current slot at this position
  existingUrl?: string;
  error?: string;
}

export interface ExistingTargetItem {
  item: DraftImageItem;
  url: string;
}

interface LocalizeScreenshotsModalProps {
  isOpen: boolean;
  appId: number;
  sourceLang: string;
  targetLang: string;
  sourceItems: { key: string; url: string }[];
  /** The target locale's current phoneScreenshots (draft or live), positionally
   *  aligned with sourceItems. Untouched slots are kept as-is on save, so a live
   *  screenshot stays a live reference and a failed slot is not re-done. */
  existingTarget?: ExistingTargetItem[];
  onClose: () => void;
  onDone: () => void;
}

export function LocalizeScreenshotsModal({
  isOpen, appId, sourceLang, targetLang, sourceItems, existingTarget = [], onClose, onDone,
}: LocalizeScreenshotsModalProps) {
  const [rows, setRows] = useState<Row[]>([]);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const rtl = isRtlLocale(targetLang);

  // On open: seed rows. A position the target locale already has content for is
  // unchecked by default (its current image is shown) so a revisit re-renders
  // nothing unless asked; a position with no target content yet is checked. Then
  // OCR (cached server-side) + transcreate each source screenshot.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setError(null);
    setSavedNote(null);
    setRows(sourceItems.map((it, i) => {
      const ex = existingTarget[i];
      return {
        key: it.key, url: it.url, english: '', translated: '',
        selected: !ex,
        loading: true, status: 'idle',
        existing: ex?.item, existingUrl: ex?.url,
      };
    }));
    (async () => {
      for (let i = 0; i < sourceItems.length; i++) {
        const it = sourceItems[i];
        try {
          const { caption } = await ocrCaption(appId, { imageKey: it.key, imageUrl: it.url, sourceLang });
          let translated = '';
          if (caption.trim()) translated = (await translateCaption({ sourceLang, targetLang, text: caption })).text;
          if (cancelled) return;
          setRows((prev) => prev.map((r) => r.key === it.key ? { ...r, english: caption, translated, loading: false } : r));
        } catch (e: any) {
          if (cancelled) return;
          setRows((prev) => prev.map((r) => r.key === it.key ? { ...r, loading: false, error: e?.message ?? 'OCR/translate failed' } : r));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, appId, sourceLang, targetLang, sourceItems, existingTarget]);

  const setField = (key: string, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => r.key === key ? { ...r, ...patch } : r));

  const selectedCount = rows.filter((r) => r.selected).length;

  // The full draft in source order: this session's result where we have one,
  // else the target's existing slot (kind preserved), else nothing for that
  // position. Every entry is submit-safe — session results are R2 draft media,
  // existing items keep their original kind.
  const assemble = (current: Row[]): DraftImageItem[] =>
    current
      .map((r): DraftImageItem | null => {
        if (r.result) return { kind: 'media', key: r.result.key, contentType: r.result.contentType, url: r.result.url };
        if (r.existing) return r.existing;
        return null;
      })
      .filter((x): x is DraftImageItem => x !== null);

  // Render only the selected rows (one worker call each) and store the result on
  // the row. A failed caption render falls back to a byte copy of the English
  // original (empty caption = no provider call) so the slot is still filled and
  // submit-safe. The draft is saved from every slot afterwards, so one failure
  // never discards the already-rendered ones.
  const renderSelected = async () => {
    setError(null);
    setSavedNote(null);
    const targets = rows.filter((r) => r.selected);
    if (targets.length === 0) return;
    setProgress({ done: 0, total: targets.length });

    let failures = 0;
    let latest = rows;
    const patch = (key: string, p: Partial<Row>) => {
      latest = latest.map((x) => x.key === key ? { ...x, ...p } : x);
      setRows(latest);
    };

    for (let i = 0; i < targets.length; i++) {
      const r = targets[i];
      patch(r.key, { status: 'rendering', error: undefined });
      try {
        const out = await renderScreenshot(appId, { imageUrl: r.url, imageKey: r.key, caption: r.translated, targetLang });
        patch(r.key, { status: 'done', selected: false, result: { key: out.key, url: out.url, contentType: out.contentType, localized: !!r.translated.trim() } });
      } catch (e: any) {
        failures++;
        // Fall back to the English original as a real copy so the slot is filled.
        try {
          const passthrough = await renderScreenshot(appId, { imageUrl: r.url, imageKey: r.key, caption: '', targetLang });
          patch(r.key, {
            status: 'error', result: { key: passthrough.key, url: passthrough.url, contentType: passthrough.contentType, localized: false },
            error: `render failed — kept English original: ${e?.message ?? ''}`.trim(),
          });
        } catch {
          patch(r.key, { status: 'error', error: e?.message ?? 'render failed' });
        }
      }
      setProgress({ done: i + 1, total: targets.length });
    }
    setProgress(null);

    try {
      await saveImageDraft(appId, targetLang, 'phoneScreenshots', assemble(latest));
      const localized = latest.filter((r) => r.result?.localized || (r.existing && !r.result)).length;
      setSavedNote(`Saved — ${localized}/${latest.length} localized.`);
      onDone();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save the localized screenshots draft');
      return;
    }
    if (failures > 0) {
      setError(`${failures} screenshot(s) failed and kept the English original — fix the caption and re-render just those.`);
    }
  };

  const anyLoading = rows.some((r) => r.loading);
  const allSelected = rows.length > 0 && rows.every((r) => r.selected);
  const thumbOf = (r: Row) => r.result?.url ?? r.existingUrl ?? r.url;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Localize screenshots → ${targetLang}`} size="lg">
      <p className="text-xs text-content-muted mb-3">
        The English caption is read from each {sourceLang} screenshot and transcreated for {targetLang}.
        Tick only the screenshots you want to render — a failed one keeps the English original, so you can
        re-render just it without redoing the rest. Saved as a draft — submit separately.
      </p>
      <div className="flex items-center gap-3 mb-2">
        <label className="flex items-center gap-1.5 text-xs text-content-muted">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={(e) => setRows((prev) => prev.map((r) => ({ ...r, selected: e.target.checked })))}
          />
          Select all
        </label>
        <span className="text-xs text-content-subtle">{selectedCount} selected</span>
      </div>
      <div className="space-y-3 max-h-[26rem] overflow-y-auto">
        {rows.map((r) => (
          <div key={r.key} className="flex gap-3 items-start border border-border rounded-md p-2">
            <label className="pt-1">
              <input type="checkbox" checked={r.selected} onChange={(e) => setField(r.key, { selected: e.target.checked })} />
            </label>
            <div className="relative shrink-0">
              <img src={thumbOf(r)} alt="" className="h-28 rounded border border-border object-cover" />
              {r.result?.localized && (
                <span className="absolute -top-1.5 -right-1.5 px-1 py-px rounded text-[10px] bg-green-600 text-white">✓</span>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <div>
                <label className="text-xs text-content-muted">English caption</label>
                <input
                  className="w-full px-2 py-1 rounded bg-background border border-border text-content text-sm"
                  value={r.english}
                  placeholder={r.loading ? 'Reading…' : 'No caption detected'}
                  onChange={(e) => setField(r.key, { english: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs text-content-muted">{targetLang} caption</label>
                <input
                  dir={rtl ? 'rtl' : 'ltr'}
                  className="w-full px-2 py-1 rounded bg-background border border-border text-content text-sm"
                  value={r.translated}
                  placeholder={r.loading ? 'Translating…' : 'Leave empty to keep the original image'}
                  onChange={(e) => setField(r.key, { translated: e.target.value })}
                />
              </div>
              {r.status === 'rendering' && <span className="text-xs text-amber-400">Rendering…</span>}
              {r.status === 'done' && r.result?.localized && <span className="text-xs text-green-500">Localized</span>}
              {r.error && <span className="text-xs text-red-400">{r.error}</span>}
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-content-muted">No source screenshots to localize.</p>}
      </div>
      {savedNote && <p className="text-green-500 text-sm mt-2">{savedNote}</p>}
      {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="secondary" onClick={onClose}>Close</Button>
        <Button
          loading={!!progress}
          disabled={anyLoading || selectedCount === 0 || !!progress}
          onClick={renderSelected}
        >
          {progress ? `Rendering ${progress.done}/${progress.total}…` : `Localize selected (${selectedCount})`}
        </Button>
      </div>
    </Modal>
  );
}
